'use client'

import React, { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, ChevronDown, ChevronUp } from 'lucide-react'
import Link from 'next/link'
import { Logo } from '@/components/Logo/Logo'
import { useLanguage } from '../../providers/LanguageProvider'

interface SidebarProps {
  open: boolean
  onClose: () => void
  data: any
}

export const Sidebar: React.FC<SidebarProps> = ({ open, onClose, data }) => {
  const { language, setLanguage } = useLanguage()
  const isArabic = language === 'ar'
  const [expandedSectionIndex, setExpandedSectionIndex] = useState<number | null>(null)
  const [expandedSubsectionId, setExpandedSubsectionId] = useState<string | null>(null)
  const [expandedSubSubsectionId, setExpandedSubSubsectionId] = useState<string | null>(null)

  useEffect(() => {
    const root = document.documentElement // <html>
    if (open) {
      document.body.classList.add('overflow-hidden')
      root.classList.add('overflow-hidden')
    } else {
      setExpandedSectionIndex(null)
      setExpandedSubsectionId(null)
      setExpandedSubSubsectionId(null)
      document.body.classList.remove('overflow-hidden')
      root.classList.remove('overflow-hidden')
    }

    return () => {
      document.body.classList.remove('overflow-hidden')
      root.classList.remove('overflow-hidden')
    }
  }, [open])

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/40 z-40"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          />

          <motion.aside
            className="fixed top-0 left-0 h-full w-full bg-white z-50 flex flex-col overflow-y-auto"
            initial={{ x: isArabic ? '100%' : '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: isArabic ? '100%' : '-100%' }}
            transition={{ duration: 0.3 }}
          >
            <div className="p-6 flex items-center justify-between">
              <Link href="/" onClick={onClose}>
                <Logo
                  isArabic={isArabic}
                  variant="dark"
                  className="md:w-[150px] md:h-[38px] w-[120px] h-[30px]"
                  loading="eager"
                  priority="high"
                />
              </Link>
              <button onClick={onClose}>
                <X strokeWidth={1.5} className="size-8" />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto scrollbar-none">
              {data?.map((item: any, i: number) => (
                <div
                  key={i}
                  className={`border-gray-200 ${expandedSectionIndex === i ? '' : data.length - 1 === i ? 'border-b' : 'border-b'}`}
                >
                  {item.needHoverMenu ? (
                    <>
                      <div
                        className={`${expandedSectionIndex === i && 'bg-gray-100 border-b-[1px] border-black'} w-full text-left block px-6 py-4 text-black/80 font-light text-[18px]`}
                      >
                        <div className="flex items-center justify-between">
                          <Link onClick={onClose} href={item.link.url}>
                            <span>{item.link.label}</span>
                          </Link>
                          {item.sections?.length > 0 && (
                            <button
                              onClick={() => {
                                if (expandedSectionIndex === i) {
                                  setExpandedSectionIndex(null)
                                } else {
                                  setExpandedSectionIndex(i)
                                }
                                // Reset all sublevels regardless
                                setExpandedSubsectionId(null)
                                setExpandedSubSubsectionId(null)
                              }}
                            >
                              {expandedSectionIndex === i ? (
                                <ChevronUp strokeWidth={1} className="ml-2 size-6 text-black" />
                              ) : (
                                <ChevronDown strokeWidth={1} className="ml-2 size-6 text-black" />
                              )}
                            </button>
                          )}
                        </div>
                      </div>

                      {expandedSectionIndex === i && item.sections?.length > 0 && (
                        <motion.div
                          key="section-expand"
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.3, ease: 'easeInOut' }}
                          layout="position"
                        >
                          {item.sections.map((section: any, i: number) => (
                            <div
                              key={section.id}
                              className={`border-gray-200 ${expandedSubsectionId === section.id ? '' : item.length - 1 === i ? 'border-b' : 'border-b'}`}
                            >
                              <button
                                onClick={() => {
                                  if (expandedSubsectionId === section.id) {
                                    setExpandedSubsectionId(null)
                                  } else {
                                    setExpandedSubsectionId(section.id)
                                  }
                                  // Reset sub-subsection always
                                  setExpandedSubSubsectionId(null)
                                }}
                                className={`${expandedSubsectionId == section.id && 'bg-gray-100 border-b-[1px] border-black'} w-full text-left block px-6 py-4 text-black/80 font-light text-[15px]`}
                              >
                                <div className="flex items-center justify-between">
                                  {section.sectionLink ? (
                                    <Link href={section.sectionLink} onClick={onClose}>
                                      <span className=".text-black\/60 hover:underline">
                                        {section.sectionText}
                                      </span>
                                    </Link>
                                  ) : (
                                    <span>{section.sectionText}</span>
                                  )}

                                  {section.subsections?.length > 0 &&
                                    (expandedSubsectionId === section.id ? (
                                      <ChevronUp
                                        strokeWidth={1}
                                        className="ml-2 size-6 text-black"
                                      />
                                    ) : (
                                      <ChevronDown
                                        strokeWidth={1}
                                        className="ml-2 size-6 text-black"
                                      />
                                    ))}
                                </div>
                              </button>

                              {expandedSubsectionId === section.id && (
                                <motion.div
                                  key="subsection-expand"
                                  initial={{ opacity: 0, y: -10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, y: -10 }}
                                  transition={{ duration: 0.3, ease: 'easeOut' }}
                                  layout="position"
                                  className=""
                                >
                                  {section.subsections.map((sub: any, i: number) => (
                                    <div
                                      key={sub.id}
                                      className={`border-gray-200 ${expandedSubSubsectionId === sub.id ? '' : section.length - 1 === i ? 'border-b' : 'border-b'}`}
                                    >
                                      <button
                                        onClick={() => {
                                          setExpandedSubSubsectionId(
                                            sub.id === expandedSubSubsectionId ? null : sub.id,
                                          )
                                        }}
                                        className={`${expandedSubSubsectionId == sub.id && 'bg-gray-100 border-b-[1px] border-black'} w-full text-left block px-6 py-4 text-black/80 font-light text-[14px]`}
                                      >
                                        <div className="flex ml-3 items-center justify-between">
                                          <Link href={sub.subsectionLink} onClick={onClose}>
                                            <span>{sub.subsectionText}</span>
                                          </Link>
                                          {sub.subSubsections?.length > 0 &&
                                            (expandedSubSubsectionId === sub.id ? (
                                              <ChevronUp
                                                strokeWidth={1}
                                                className="ml-2 size-6 text-black"
                                              />
                                            ) : (
                                              <ChevronDown
                                                strokeWidth={1}
                                                className="ml-2 size-6 text-black"
                                              />
                                            ))}
                                        </div>
                                      </button>

                                      {expandedSubSubsectionId === sub.id && (
                                        <motion.div
                                          key="subsubsection-expand"
                                          initial={{ opacity: 0, y: -10 }}
                                          animate={{ opacity: 1, y: 0 }}
                                          exit={{ opacity: 0, y: -10 }}
                                          transition={{ duration: 0.3, ease: 'easeOut' }}
                                          layout="position"
                                          className=""
                                        >
                                          {sub.subSubsections?.map((ss: any, i: number) => {
                                            return (
                                              <div
                                                key={ss.id}
                                                className={`${item.length - 1 === i ? '' : 'border-b border-gray-200'} pl-5`}
                                              >
                                                <Link
                                                  key={ss.id}
                                                  href={ss.subSubsectionLink}
                                                  onClick={onClose}
                                                  className="w-full text-left block px-6 py-4 text-black/80 font-light text-[14px]"
                                                >
                                                  {ss.subSubsectionText}
                                                </Link>
                                              </div>
                                            )
                                          })}
                                        </motion.div>
                                      )}
                                    </div>
                                  ))}
                                </motion.div>
                              )}
                            </div>
                          ))}
                        </motion.div>
                      )}
                    </>
                  ) : (
                    <span onClick={onClose}>
                      <Link
                        href={item.link.url}
                        className="block px-6 py-4 text-black/80 font-light text-[18px]"
                      >
                        {item.link.label}
                      </Link>
                    </span>
                  )}
                </div>
              ))}
            </nav>

            <div className="p-6">
              <Link href="/get-a-quote">
                <button
                  onClick={onClose}
                  className="w-full relative group bg-[#EE3E25] hover:bg-transparent duration-1000 text-white text-sm font-semibold py-3 px-6 rounded transition-all"
                >
                  <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 size-2 bg-white rounded-full z-0 opacity-0 scale-0 group-hover:opacity-100 group-hover:scale-[25] transition-all duration-700 ease-in-out" />
                  <span className="relative z-10 group-hover:text-[#EE3E25]">
                    {isArabic ? 'طلب عرض' : 'GET A QUOTE'}
                  </span>
                </button>
              </Link>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  )
}
